import { useQueries, useQueryClient } from '@tanstack/react-query';

import { defaultWorkspaceName } from '../../../aiem/defaults';
import { useWorkspaces } from '../../../pages/Orchestrator/WorkspaceProvider';
import { useCloudPlatformDiscovery, useUser } from '../../../utils/hooks';
import { roleBindingResourceCompile } from '../../authz.functions';
import { getQueryOptionsPermission } from '../../authz.hooks';
import { userName } from '../../gen/ai/h2o/user/v1/user_pb';

export const restrictionTooltipMessage = (showTooltip: boolean) =>
  showTooltip ? 'You are not authorized to perform this action.' : '';

export const permissionBindingsParam = `//workspaceserver/`;

const usePermissions = (actions: string[] = [], resourceParam?: string): boolean[] => {
  const { ACTIVE_WORKSPACE_NAME } = useWorkspaces();
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const currentUser = useUser();
  const currentUserName = userName.compile({ user: currentUser.id });
  const getResource = (resourceParam?: string): string => {
    if (!resourceParam) {
      return roleBindingResourceCompile(ACTIVE_WORKSPACE_NAME || defaultWorkspaceName);
    }

    if (resourceParam.startsWith('workspaces/')) {
      return `${permissionBindingsParam}${resourceParam}`;
    }

    return resourceParam;
  };

  const resource = getResource(resourceParam);
  const queryClient = useQueryClient();
  const queries = actions.map((action) => {
    const baseQueryOptions = getQueryOptionsPermission(queryClient, authzUrl, {
      subject: currentUserName,
      action,
      resource,
    });
    return {
      ...baseQueryOptions,
      enabled: !currentUser.isPublic,
    };
  });
  const queryResults = useQueries({ queries });
  const result: boolean[] = [];
  let loading = false;
  for (const query of queryResults) {
    if (query.isLoading) {
      loading = true;
    }
    result.push(Boolean(query.data?.allowed));
  }
  // TODO: change this "last entry is loading state" to a better solution
  return result.concat(loading);
};

export { usePermissions };
